/**
 * Kreator — podgląd "na żywo" JEDNEGO widoku encji (formularz albo lista) w
 * pełnoekranowym modalu (`PreviewModal`). Renderuje WIERNIE wobec klienta:
 * formularz przez `PreviewFormSections` (siatka sekcji 1:1 z `RecordFormPage`),
 * lista przez `ConfigurableTable` — zasilone syntetycznymi danymi próbkowymi
 * (`lib/preview/generate-sample-data.ts`), bez sieci i zapisu, przed instalacją.
 *
 * Read-only z założenia: `ConfigurableTable` dostaje `persistConfig={false}`
 * (nie odpytuje ani nie zapisuje layoutu pod `tableId="preview-…"`), a formularz
 * renderuje wartości read-only (`renderPreviewFieldValue`), bez inputów.
 *
 * @see apps/admin/src/components/preview/PreviewFormSections.tsx — render formularza
 * @see apps/admin/src/lib/preview/ — generator próbek + adapter kolumn listy
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ModuleTemplateFieldDefinition, ModuleTemplateViewDefinition } from '@saas/shared';

import { ConfigurableTable } from '@/components/configurable-table';
import { PreviewTenantAuthBoundary } from '@/contexts/TenantAuthContext';
import { generateSampleRecords } from '@/lib/preview/generate-sample-data';
import { buildPreviewColumns } from '@/lib/preview/list-preview-adapter';

import { PreviewFormSections } from './PreviewFormSections';
import { PreviewModal } from './PreviewModal';

// 4 rekordy = jeden pełny rząd kafelków (widok „Karty" klienta kapuje na 4 kolumny
// przy szerokim ekranie: `xl:grid-cols-4`) — bez sierocego kafelka w drugim rzędzie.
const LIST_PREVIEW_RECORD_COUNT = 4;

export interface EntityViewPreviewProps {
  open: boolean;
  onClose: () => void;
  view: ModuleTemplateViewDefinition;
  fields: ModuleTemplateFieldDefinition[];
}

/**
 * Podgląd widoku `form` — jeden syntetyczny rekord, renderowany siatką sekcji
 * 1:1 z klientem (`RecordFormPage`), przez `PreviewFormSections`.
 */
function FormViewPreview({
  view,
  fields,
}: {
  view: ModuleTemplateViewDefinition;
  fields: ModuleTemplateFieldDefinition[];
}) {
  const sample = useMemo(() => generateSampleRecords(fields, 1)[0] ?? {}, [fields]);
  return <PreviewFormSections fields={fields} formConfig={view.formConfig} sample={sample} />;
}

/**
 * Podgląd widoku `list` — kilka syntetycznych wierszy w `ConfigurableTable`
 * (read-only: `persistConfig={false}` — nie odpytuje ani nie zapisuje layoutu).
 */
function ListViewPreview({
  view,
  fields,
}: {
  view: ModuleTemplateViewDefinition;
  fields: ModuleTemplateFieldDefinition[];
}) {
  const data = useMemo(() => generateSampleRecords(fields, LIST_PREVIEW_RECORD_COUNT), [fields]);
  const columns = useMemo(
    () => buildPreviewColumns(fields, view.listConfig),
    [fields, view.listConfig],
  );

  return (
    <ConfigurableTable
      tableId={`preview-${view.slug}`}
      columns={columns}
      data={data}
      getRowId={(row) => String(row.id)}
      persistConfig={false}
      showViewModeToggle={false}
    />
  );
}

export function EntityViewPreview({ open, onClose, view, fields }: EntityViewPreviewProps) {
  const { t } = useTranslation();

  if (!open) return null;

  return (
    <PreviewModal
      open={open}
      onClose={onClose}
      title={t('moduleTemplateEditor.preview.title', { name: view.name })}
    >
      <PreviewTenantAuthBoundary>
        {view.type === 'form' ? (
          <FormViewPreview view={view} fields={fields} />
        ) : view.type === 'list' ? (
          <ListViewPreview view={view} fields={fields} />
        ) : null}
      </PreviewTenantAuthBoundary>
    </PreviewModal>
  );
}
